/**
 * Visor SVG de un tablero (réplica de `sheetSVG()` del SSR `cutlist.js`).
 *
 * Dibuja una lámina con sus piezas colocadas (color + dimensiones + etiqueta), los
 * rectángulos de desperdicio, los segmentos de corte (naranja = horizontal, gris =
 * vertical), los marcadores de columna/fila y las cotas rojas del contorno. Igual que
 * el SSR, pero con elementos React (SVG inline, sin dependencias pesadas).
 *
 * Presentacional puro: recibe el `Sheet` y la `scale` ya calculada; no hace `fetch` ni
 * contiene lógica de negocio (`docs/frontend_architecture.md`, Pr. 6). Los tooltips de
 * pieza se delegan al `title` nativo del SVG (accesible y testeable) en vez del tooltip
 * flotante manual del SSR.
 */
import { Fragment } from "react";

import { SVG_PAD as PAD } from "../constants";
import { darken, rgba, roundCm } from "../format";
import { clampPlacementsToBoard } from "../optimizer";
import type { Sheet } from "../types";

interface SheetSvgProps {
  sheet: Sheet;
  scale: number;
}

/** Segmentos únicos ordenados a partir de los bordes de las piezas (marcadores). */
function uniqueSorted(values: number[]): number[] {
  return Array.from(new Set(values)).sort((a, b) => a - b);
}

export function SheetSvg({ sheet, scale }: SheetSvgProps) {
  const W = sheet.stock.width;
  const H = sheet.stock.height;
  const sw = W * scale;
  const sh = H * scale;
  const vw = sw + PAD * 2 + 20;
  const vh = sh + PAD * 2 + 20;

  // Defensa en profundidad: saneamos los placements contra el tablero. Con el packing
  // actual nunca se salen, pero el `result` puede venir de un snapshot del historial
  // (recargado sin re-validar en `CutlistScreen`); recortamos/descartamos lo que
  // sobresalga para no dibujar piezas fuera de la superficie de la lámina.
  const placements = clampPlacementsToBoard(sheet.placements, W, H);

  // Id de clip único por tablero, para recortar todo lo pintado sobre la superficie.
  const clipId = `sheet-clip-${sheet.id}`;

  const xCuts = uniqueSorted([
    0,
    W,
    ...placements.map((p) => p.x),
    ...placements.map((p) => p.x + p.placedWidth),
  ]);
  const yCuts = uniqueSorted([
    0,
    H,
    ...placements.map((p) => p.y),
    ...placements.map((p) => p.y + p.placedHeight),
  ]);
  const colSegs = xCuts
    .slice(0, -1)
    .map((x, i) => ({ x, w: xCuts[i + 1] - x }))
    .filter((s) => s.w > 0.5);
  const rowSegs = yCuts
    .slice(0, -1)
    .map((y, i) => ({ y, h: yCuts[i + 1] - y }))
    .filter((s) => s.h > 0.5);

  return (
    <svg
      width={vw}
      height={vh}
      role="img"
      aria-label={`Tablero ${roundCm(W)}×${roundCm(H)} cm con ${placements.length} piezas`}
      style={{ fontFamily: "'Inter','Segoe UI',system-ui,sans-serif" }}
    >
      {/* Recorte a la superficie de la lámina: nada pintado sobre el tablero (piezas,
          desperdicio, cortes) puede salirse del borde, aunque llegara un dato malo. */}
      <defs>
        <clipPath id={clipId}>
          <rect x={PAD} y={PAD} width={sw} height={sh} />
        </clipPath>
      </defs>

      {/* Contorno del tablero */}
      <rect
        x={PAD}
        y={PAD}
        width={sw}
        height={sh}
        fill="white"
        stroke="#9ca3af"
        strokeWidth={1.5}
      />

      {/* Contenido sobre la superficie (recortado al tablero) */}
      <g clipPath={`url(#${clipId})`}>
      {/* Áreas de desperdicio */}
      {(sheet.wasteRects || []).map((wr, i) => {
        if (wr.width < 0.5 || wr.height < 0.5) return null;
        const wx = PAD + wr.x * scale,
          wy = PAD + wr.y * scale;
        const ww = wr.width * scale,
          wh = wr.height * scale;
        const dimFs = Math.max(7, Math.min(11, Math.min(ww, wh) * 0.2));
        return (
          <Fragment key={`w-${i}`}>
            <rect
              x={wx}
              y={wy}
              width={ww}
              height={wh}
              fill="#f1f3f5"
              stroke="#ced4da"
              strokeWidth={0.8}
            />
            {ww > 16 ? (
              <text
                x={wx + ww / 2}
                y={wy + dimFs + 2}
                textAnchor="middle"
                fontSize={dimFs}
                fontWeight={600}
                fill="#6b7280"
              >
                {roundCm(wr.width)}
              </text>
            ) : null}
            {wh > 16 ? (
              <text
                x={wx + dimFs + 2}
                y={wy + wh / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={dimFs}
                fontWeight={600}
                fill="#6b7280"
                transform={`rotate(-90,${wx + dimFs + 2},${wy + wh / 2})`}
              >
                {roundCm(wr.height)}
              </text>
            ) : null}
          </Fragment>
        );
      })}

      {/* Piezas colocadas */}
      {placements.map((p, i) => {
        const px = PAD + p.x * scale,
          py = PAD + p.y * scale;
        const pw = p.placedWidth * scale,
          ph = p.placedHeight * scale;
        const dimFs = Math.max(7, Math.min(11, Math.min(pw, ph) * 0.2));
        const labelFs = Math.max(6, Math.min(10, Math.min(pw, ph) * 0.16));
        return (
          <g key={`p-${i}`} style={{ cursor: "crosshair" }}>
            <title>
              {`${p.label} — ${roundCm(p.placedWidth)} × ${roundCm(p.placedHeight)} cm${p.rotated ? " (rotado)" : ""}`}
            </title>
            <rect
              x={px}
              y={py}
              width={pw}
              height={ph}
              fill={rgba(p.color, 0.28)}
              stroke={darken(p.color, 20)}
              strokeWidth={1.2}
            />
            {pw > 28 && ph > 22 ? (
              <text
                x={px + pw / 2}
                y={py + ph / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={labelFs}
                fill={darken(p.color, 60)}
                opacity={0.6}
              >
                {p.label}
              </text>
            ) : null}
            {pw > 16 ? (
              <text
                x={px + pw / 2}
                y={py + dimFs + 2}
                textAnchor="middle"
                fontSize={dimFs}
                fontWeight={600}
                fill="#1e40af"
              >
                {roundCm(p.placedWidth)}
              </text>
            ) : null}
            {ph > 16 ? (
              <text
                x={px + dimFs + 2}
                y={py + ph / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={dimFs}
                fontWeight={600}
                fill="#1e40af"
                transform={`rotate(-90,${px + dimFs + 2},${py + ph / 2})`}
              >
                {roundCm(p.placedHeight)}
              </text>
            ) : null}
          </g>
        );
      })}

      {/* Cortes */}
      {(sheet.cutSegments || []).map((seg, i) => (
        <line
          key={`c-${i}`}
          x1={PAD + seg.x1 * scale}
          y1={PAD + seg.y1 * scale}
          x2={PAD + seg.x2 * scale}
          y2={PAD + seg.y2 * scale}
          stroke={seg.type === "h" ? "#f97316" : "#94a3b8"}
          strokeWidth={seg.type === "h" ? 0.9 : 0.6}
          strokeDasharray={seg.type === "h" ? "5,3" : "3,3"}
          opacity={0.7}
        />
      ))}
      </g>

      {/* Marcadores de columna (arriba) */}
      {colSegs.map((seg, i) => {
        const x1 = PAD + seg.x * scale,
          x2 = PAD + (seg.x + seg.w) * scale;
        const xm = (x1 + x2) / 2,
          y = PAD - 6;
        return (
          <Fragment key={`col-${i}`}>
            <line x1={x1} y1={y - 3} x2={x2} y2={y - 3} stroke="#374151" strokeWidth={0.7} />
            <line x1={x1} y1={y - 5} x2={x1} y2={y - 1} stroke="#374151" strokeWidth={0.7} />
            <line x1={x2} y1={y - 5} x2={x2} y2={y - 1} stroke="#374151" strokeWidth={0.7} />
            <text x={xm} y={y - 6} textAnchor="middle" fontSize={8} fill="#374151" fontWeight={600}>
              {roundCm(seg.w)}
            </text>
          </Fragment>
        );
      })}

      {/* Marcadores de fila (izquierda) */}
      {rowSegs.map((seg, i) => {
        const y1 = PAD + seg.y * scale,
          y2 = PAD + (seg.y + seg.h) * scale;
        const ym = (y1 + y2) / 2,
          x = PAD - 6;
        return (
          <Fragment key={`row-${i}`}>
            <line x1={x - 3} y1={y1} x2={x - 3} y2={y2} stroke="#374151" strokeWidth={0.7} />
            <line x1={x - 5} y1={y1} x2={x - 1} y2={y1} stroke="#374151" strokeWidth={0.7} />
            <line x1={x - 5} y1={y2} x2={x - 1} y2={y2} stroke="#374151" strokeWidth={0.7} />
            <text
              x={x - 6}
              y={ym}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize={8}
              fill="#374151"
              fontWeight={600}
              transform={`rotate(-90,${x - 6},${ym})`}
            >
              {roundCm(seg.h)}
            </text>
          </Fragment>
        );
      })}

      {/* Cota roja del alto (derecha) */}
      <line x1={PAD + sw + 8} y1={PAD} x2={PAD + sw + 8} y2={PAD + sh} stroke="#dc2626" strokeWidth={1} />
      <line x1={PAD + sw + 5} y1={PAD} x2={PAD + sw + 11} y2={PAD} stroke="#dc2626" strokeWidth={1} />
      <line x1={PAD + sw + 5} y1={PAD + sh} x2={PAD + sw + 11} y2={PAD + sh} stroke="#dc2626" strokeWidth={1} />
      <text
        x={PAD + sw + 20}
        y={PAD + sh / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={9}
        fill="#dc2626"
        fontWeight={700}
        transform={`rotate(90,${PAD + sw + 20},${PAD + sh / 2})`}
      >
        {roundCm(H)}
      </text>

      {/* Cota roja del ancho (abajo) */}
      <line x1={PAD} y1={PAD + sh + 8} x2={PAD + sw} y2={PAD + sh + 8} stroke="#dc2626" strokeWidth={1} />
      <line x1={PAD} y1={PAD + sh + 5} x2={PAD} y2={PAD + sh + 11} stroke="#dc2626" strokeWidth={1} />
      <line x1={PAD + sw} y1={PAD + sh + 5} x2={PAD + sw} y2={PAD + sh + 11} stroke="#dc2626" strokeWidth={1} />
      <text
        x={PAD + sw / 2}
        y={PAD + sh + 20}
        textAnchor="middle"
        fontSize={9}
        fill="#dc2626"
        fontWeight={700}
      >
        {roundCm(W)}
      </text>
    </svg>
  );
}
